import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, ExternalLink, Headphones, Square } from "lucide-react";

/* IndianAirports — shared long-form article kit.
   Used by the OCI & NRI guide and the Visiting India guide.
   Section renders its own Listen button (browser TTS, section-by-section). */

export const P = ({ children }) => <p className="text-sm text-zinc-600 leading-relaxed">{children}</p>;

function speak(text, onDone) {
  const synth = window.speechSynthesis;
  if (!synth) return false;
  const u = new SpeechSynthesisUtterance(text.slice(0, 4000));
  u.rate = 0.95;
  // Traveler's chosen voice (picked in My Social Pulse briefing settings)
  try {
    const p = JSON.parse(localStorage.getItem("tp_tts") || "{}");
    if (p.voiceURI) {
      const v = synth.getVoices().find((x) => x.voiceURI === p.voiceURI);
      if (v) u.voice = v;
    }
    if (p.rate) u.rate = p.rate;
  } catch { /* default voice */ }
  u.onend = onDone;
  u.onerror = onDone;
  synth.speak(u);
  return true;
}

export const Section = ({ title, children, tid = "ia-listen" }) => {
  const ref = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const testid = `${tid}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`;

  const toggle = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking) { synth.cancel(); setSpeaking(false); return; }
    synth.cancel(); // stop any other section that is playing
    const text = `${title}. ${ref.current?.innerText || ""}`;
    if (speak(text, () => setSpeaking(false))) setSpeaking(true);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-heading font-900 text-lg md:text-lg text-zinc-900">{title}</h2>
        <button onClick={toggle} data-testid={testid} title={speaking ? "Stop" : `Listen to "${title}"`}
          className={`inline-flex items-center gap-1 text-[10px] font-800 uppercase tracking-wide rounded-full px-2 py-1 border transition-colors ${speaking ? "bg-blue-700 text-white border-blue-700" : "text-zinc-500 border-zinc-300 hover:text-blue-700 hover:border-blue-400"}`}>
          {speaking ? <Square className="w-3 h-3" /> : <Headphones className="w-3 h-3" />} {speaking ? "Stop" : "Listen"}
        </button>
      </div>
      <div ref={ref} className="space-y-3">{children}</div>
    </section>
  );
};

export const H3 = ({ children }) => <h3 className="font-800 text-sm text-zinc-800 mt-4">{children}</h3>;

export const Checks = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
    {items.map((t) => (
      <div key={t} className="flex items-start gap-1.5 text-xs text-zinc-600">
        <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-[1px]" /><span>{t}</span>
      </div>
    ))}
  </div>
);

export const Nots = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
    {items.map((t) => (
      <div key={t} className="flex items-start gap-1.5 text-xs text-zinc-600">
        <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-[1px]" /><span>{t}</span>
      </div>
    ))}
  </div>
);

export const Warn = ({ children }) => (
  <p className="text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" /><span>{children}</span>
  </p>
);

export const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-700 font-700 hover:underline inline-flex items-center gap-0.5">
    {children} <ExternalLink className="w-3 h-3" />
  </a>
);

export const Steps = ({ items, color = "bg-blue-700" }) => (
  <ol className="space-y-1.5">
    {items.map((s, i) => (
      <li key={s} className="flex items-start gap-2.5 text-sm text-zinc-600">
        <span className={`shrink-0 w-5 h-5 rounded-full ${color} text-white text-[10px] font-800 inline-flex items-center justify-center mt-0.5`}>{i + 1}</span>{s}
      </li>
    ))}
  </ol>
);
